import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { usePricingVisibility } from "@/lib/pricing-visibility";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, DollarSign, FileText, Plus, Brain, TrendingUp, Eye, Copy, Trash2, Settings, Search, Upload } from "lucide-react";
import { type ShowWithDetails, type PlanWithDetails } from "@shared/schema";
import { PlanStorage } from "@/lib/plan-storage";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/navbar";
import PlanFormNew from "@/components/plan-form-new";

export default function Plans() {
  const { user } = useAuth();
  const { isPricingVisible } = usePricingVisibility();
  const [selectedShows, setSelectedShows] = useState<ShowWithDetails[]>([]);
  const [activeTab, setActiveTab] = useState<'create' | 'manage'>('create');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: plans = [], isLoading } = useQuery<PlanWithDetails[]>({
    queryKey: ["/api/plans"],
    enabled: !!user,
  });

  const { data: stats } = useQuery<{ sponsorableShows: number }>({
    queryKey: ["/api/stats"],
  });

  // Mutation to delete a plan
  const deletePlanMutation = useMutation({
    mutationFn: async (planId: number) => {
      const response = await apiRequest(`/api/plans/${planId}`, { method: "DELETE" });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/plans"] });
      toast({
        title: "Plan Deleted",
        description: "The plan has been deleted successfully.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error Deleting Plan",
        description: "Failed to delete plan. Please try again.",
      });
    }
  });

  // Load selected shows from localStorage on mount
  useEffect(() => {
    const storedShows = PlanStorage.getSelectedShows(user?.id);
    setSelectedShows(storedShows);
  }, [user?.id]);

  const handleRemoveShow = (showId: number) => {
    PlanStorage.removeShow(showId, user?.id);
    const updatedShows = PlanStorage.getSelectedShows(user?.id);
    setSelectedShows(updatedShows);
  };

  const handleClearShows = () => {
    PlanStorage.clearShows(user?.id);
    setSelectedShows([]);
  };

  const handleLoadPlan = (plan: PlanWithDetails) => {
    // Clear existing shows first
    PlanStorage.clearShows(user?.id);

    // Load shows from the plan into PlanStorage
    if (plan.shows && plan.shows.length > 0) {
      plan.shows.forEach(show => {
        PlanStorage.addShow(show, user?.id);
      });

      // Update local state
      setSelectedShows(plan.shows);

      // Switch to Create tab
      setActiveTab('create');

      toast({
        title: "Plan loaded",
        description: `Loaded ${plan.shows.length} show${plan.shows.length > 1 ? 's' : ''} into the Plan Builder.`,
      });
    } else {
      toast({
        title: "No shows to load",
        description: "This plan doesn't have any shows.",
        variant: "destructive",
      });
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16 pt-24 max-w-6xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-max-brand to-pink-600 rounded-full mb-6">
              <FileText className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-archivo font-bold text-neutral-900 mb-6">
              Smart Sponsorship Plans
            </h1>
            <p className="text-xl text-neutral-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Create professional, AI-powered sponsorship plans that get results. Our intelligent system helps you craft compelling campaigns that connect brands with the perfect music moments.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="relative text-center p-6 bg-white rounded-xl shadow-sm border border-neutral-100 hover:shadow-lg transition-all duration-300 group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                  <Brain className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">AI-Powered Insights</h3>
                <p className="text-neutral-600 text-sm">
                  Get intelligent campaign recommendations, audience insights, and cost estimates powered by our advanced AI system.
                </p>
              </div>
            </div>
            
            <div className="relative text-center p-6 bg-white rounded-xl shadow-sm border border-neutral-100 hover:shadow-lg transition-all duration-300 group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">Performance Metrics</h3>
                <p className="text-neutral-600 text-sm">
                  Track estimated impressions, engagement rates, and ROI projections for every plan you create.
                </p>
              </div>
            </div>
            
            <div className="relative text-center p-6 bg-white rounded-xl shadow-sm border border-neutral-100 hover:shadow-lg transition-all duration-300 group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4">
                  <Plus className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">Multi-Show Campaigns</h3>
                <p className="text-neutral-600 text-sm">
                  Bundle multiple shows into comprehensive campaigns with detailed show summaries and unified pricing.
                </p>
              </div>
            </div>
          </div>

          {/* Visual Example */}
          <div className="bg-white rounded-2xl shadow-xl border border-neutral-200 p-8 mb-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-4">Professional Plans in Minutes</h3>
                <ul className="space-y-3 text-neutral-600">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-max-brand rounded-full mr-3"></div>
                    Select shows from {stats?.sponsorableShows || 0}+ sponsorable opportunities
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-max-brand rounded-full mr-3"></div>
                    Get AI-generated campaign overviews and insights
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-max-brand rounded-full mr-3"></div>
                    Review detailed metrics and audience demographics
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-max-brand rounded-full mr-3"></div>
                    Export professional plans ready to send
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-xl p-6 border-2 border-dashed border-neutral-300">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-max-brand" />
                    <span className="text-sm font-medium text-neutral-700">3 Selected Shows</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-neutral-700">$45,000 Estimated Cost</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Eye className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-neutral-700">890K+ Estimated Impressions</span>
                  </div>
                  <div className="h-px bg-neutral-200 my-3"></div>
                  <div className="text-xs text-neutral-500 italic">
                    "AI-generated campaign insights and professional formatting included"
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-gradient-to-r from-max-brand/5 via-pink-500/5 to-max-brand/5 rounded-2xl p-8 border border-max-brand/10">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">Ready to Create Winning Plans?</h2>
            <p className="text-lg text-neutral-600 mb-6 max-w-2xl mx-auto">
              Join The MAX Platform™ today with a <strong>completely free account</strong> and unlock the full power of professional sponsorship plan creation. No credit card required, no hidden fees - just results.
            </p>
            <div className="flex justify-center">
              <Link href="/register">
                <Button className="btn-max-primary px-8 py-3 text-lg font-semibold hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  Create Free Account
                </Button>
              </Link>
            </div>
            <p className="text-sm text-neutral-500 mt-4">
              Already have an account? <Link href="/login" className="text-max-brand hover:underline font-medium">Sign in here</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      
      {/* Sticky Action Bar */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-sm border-b border-neutral-200 shadow-sm">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Create and Manage buttons */}
            <div className="flex items-center space-x-1 bg-white rounded-lg p-1 border border-neutral-200 shadow-sm">
              <button
                onClick={() => setActiveTab('create')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === 'create'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                }`}
              >
                <Plus className="h-4 w-4" />
                <span>Create Plan</span>
              </button>
              <button
                onClick={() => setActiveTab('manage')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === 'manage'
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                }`}
              >
                <Settings className="h-4 w-4" />
                <span>Manage Plans</span>
              </button>
            </div>
            
            {/* Right side - Browse Shows button */}
            <Link href="/browse-artists">
              <Button className="flex items-center space-x-2 bg-primary hover:bg-primary/90">
                <Search className="h-4 w-4" />
                <span>Browse Shows</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8 max-w-6xl pt-12">
        {/* Content based on active tab */}
        {activeTab === 'create' ? (
          <PlanFormNew
            selectedShows={selectedShows}
            onRemoveShow={handleRemoveShow}
            onClearShows={handleClearShows}
          />
        ) : (
            <div className="space-y-6">
              {isLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, index) => (
                    <div key={index} className="animate-pulse">
                      <div className="bg-white rounded-lg shadow-lg p-6">
                        <div className="h-4 bg-neutral-200 rounded mb-2"></div>
                        <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2"></div>
                        <div className="h-8 bg-neutral-200 rounded w-1/4"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : plans.length === 0 ? (
                <Card>
                  <CardContent className="py-12">
                    <div className="text-center">
                      <FileText className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-neutral-900 mb-2">No plans yet</h3>
                      <p className="text-neutral-600 mb-4">
                        Start by browsing shows and creating your first plan.
                      </p>
                      <Link href="/browse-artists">
                        <Button>Browse Shows</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-8">
                  {plans.map((plan) => {
                    const totalCost = parseFloat(plan.totalBudget || '0');
                    const showCount = plan.shows?.length || 0;
                    const avgCost = showCount > 0 ? Math.round(totalCost / showCount) : 0;

                    return (
                      <Link key={plan.id} href={`/plans/${plan.id}`} className="block mb-8">
                        <Card className="group hover:shadow-xl hover:scale-[1.01] transition-all duration-300 border-pink-200/50 shadow-lg cursor-pointer overflow-hidden">
                          <CardContent className="p-0">
                            <div className="flex items-stretch">
                              {/* 1. Gradient Hero Section (left ~180px) */}
                              <div className="bg-gradient-to-br from-[#FA017C] to-[#0240FB] p-4 text-white flex flex-col justify-center min-w-[180px]">
                                <div className="relative">
                                  {isPricingVisible ? (
                                    <>
                                      <p className="text-xs font-medium opacity-90 mb-1">Total Investment</p>
                                      <p className="text-3xl font-bold mb-2">${(totalCost / 1000).toFixed(0)}k</p>
                                      <div className="pt-2 border-t border-white/20">
                                        <p className="text-sm font-medium">${(avgCost / 1000).toFixed(1)}k <span className="text-xs opacity-80">per show</span></p>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-center">
                                      <p className="text-xs font-medium opacity-90 mb-2">Plan Summary</p>
                                      <p className="text-lg font-semibold">Contact for</p>
                                      <p className="text-lg font-semibold">Pricing</p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* 2. Stats Grid (middle flex-1) */}
                              <div className="flex-1 p-4 bg-gradient-to-r from-neutral-50/50 to-white">
                                <div className="grid grid-cols-4 gap-3 mb-3">
                                  {/* Shows */}
                                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-2 border-t-2 border-blue-500 shadow-sm">
                                    <p className="text-xl font-bold text-blue-900">{showCount}</p>
                                    <p className="text-[10px] text-blue-600 font-medium">Shows</p>
                                  </div>

                                  {/* Artists */}
                                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-2 border-t-2 border-pink-500 shadow-sm">
                                    <p className="text-xl font-bold text-pink-900">
                                      {[...new Set(plan.shows?.map(show => show.artist.id))].length || 0}
                                    </p>
                                    <p className="text-[10px] text-pink-600 font-medium">Artists</p>
                                  </div>

                                  {/* Markets */}
                                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-2 border-t-2 border-purple-500 shadow-sm">
                                    <p className="text-xl font-bold text-purple-900">
                                      {[...new Set(plan.shows?.map(show => show.venue.market?.name || 'Unknown').filter(m => m !== 'Unknown'))].length || 0}
                                    </p>
                                    <p className="text-[10px] text-purple-600 font-medium">Markets</p>
                                  </div>

                                  {/* States */}
                                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-2 border-t-2 border-cyan-500 shadow-sm">
                                    <p className="text-xl font-bold text-cyan-900">
                                      {[...new Set(plan.shows?.map(show => show.venue.state || 'Unknown').filter(s => s !== 'Unknown'))].length || 0}
                                    </p>
                                    <p className="text-[10px] text-cyan-600 font-medium">States</p>
                                  </div>
                                </div>

                                {/* Duration & Attendees Row */}
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Duration */}
                                  {plan.shows && plan.shows.length > 0 && (() => {
                                    const sortedShows = [...plan.shows].sort((a, b) =>
                                      new Date(a.date).getTime() - new Date(b.date).getTime()
                                    );
                                    const firstDate = new Date(sortedShows[0].date);
                                    const lastDate = new Date(sortedShows[sortedShows.length - 1].date);
                                    const days = Math.ceil((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));

                                    return (
                                      <div className="bg-white/80 backdrop-blur-sm rounded-lg p-2 border-l-2 border-amber-500 shadow-sm flex items-center gap-2">
                                        <div className="flex-1">
                                          <p className="text-lg font-bold text-amber-900">{days}</p>
                                          <p className="text-[10px] text-amber-600 font-medium">{days === 1 ? 'Day' : 'Days'}</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-[9px] text-amber-700">
                                            {firstDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {lastDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* Live Attendees */}
                                  <div className="bg-white/80 backdrop-blur-sm rounded-lg p-2 border-l-2 border-green-500 shadow-sm flex items-center justify-between">
                                    <div>
                                      <p className="text-lg font-bold text-green-900">
                                        {plan.shows ? Math.round(plan.shows.reduce((sum, show) => sum + (show.venue.capacity || 0), 0) / 1000) : 0}k
                                      </p>
                                      <p className="text-[10px] text-green-600 font-medium">Attendees</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* 3. Plan Details Section (middle-right ~280px) */}
                              <div className="min-w-[280px] p-4 bg-white border-l border-neutral-200">
                                <h3 className="text-lg font-bold text-neutral-900 mb-2 group-hover:text-max-brand transition-colors truncate">
                                  {plan.campaignName}
                                </h3>
                                <div className="space-y-2 text-sm">
                                  {plan.brand && (
                                    <div className="flex items-center gap-2 text-neutral-700">
                                      <DollarSign className="h-3 w-3 text-neutral-500" />
                                      <span className="truncate font-medium">{plan.brand}</span>
                                    </div>
                                  )}
                                  {plan.targetAudience && (
                                    <div className="flex items-center gap-2 text-neutral-700">
                                      <TrendingUp className="h-3 w-3 text-neutral-500" />
                                      <span className="truncate">{plan.targetAudience}</span>
                                    </div>
                                  )}
                                  {plan.campaignGoals && (
                                    <div className="flex items-center gap-2 text-neutral-700">
                                      <Eye className="h-3 w-3 text-neutral-500" />
                                      <span className="capitalize truncate">{plan.campaignGoals}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 text-neutral-500 text-xs pt-2 border-t border-neutral-200">
                                    <Calendar className="h-3 w-3" />
                                    <span>Saved {plan.createdAt ? formatDate(plan.createdAt) : 'Unknown'}</span>
                                  </div>
                                </div>
                              </div>

                              {/* 4. Action Buttons (right ~120px) */}
                              <div className="min-w-[120px] p-4 bg-neutral-50 border-l border-neutral-200 flex flex-col justify-center gap-2">
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="w-full text-xs"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Open Plan
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (confirm(`Delete "${plan.campaignName}"?`)) {
                                      deletePlanMutation.mutate(plan.id);
                                    }
                                  }}
                                  disabled={deletePlanMutation.isPending}
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
        )}
      </div>
    </div>
  );
}
